import { css, html, LitElement } from "lit";

import "../components/navbar";
import "../components/button";
import "../components/landing";
import "../components/flight";

import { DOWNLOAD_URL } from "../../constants";
import {
  fetchLatestFlights,
  fetchLatestLandings,
  fetchTotalMiles,
} from "../common/api";
import { setTitle, trackPage } from "../common/client";
import { websiteStyles } from "../styles/website";

export class HomePage extends LitElement {
  constructor() {
    super();
    this.totalMiles = 5500000;
    this.timer = null;
    /** @type {Array<LandingRateData>} */
    this.landings = [];
    /** @type {Array<FlightSessionData>} */
    this.latestFlights = [];
  }

  static styles = [
    websiteStyles,
    css`
      :host {
        display: block;
        width: 100vw;
        height: 100vh;
        background: #FFF;
        color: #000;
        padding-top: 70px;
      }     

      button-component {
        z-index: 2;
      }      

      .bubble {
        cursor: pointer;
        color: #fff;
        background-color: #333;
        padding: 10px 10px;
        border-radius: 5px;
        margin: 3px;
        text-transform: uppercase;
        display: inline-block;
        text-decoration: none;
        font-size: 1.2rem;
        font-weight: 600;
      }

      .bubble:hover {
        background-color: #444;
      }

      .hero {
          background-image: url("./images/screen1.webp");
          background-size: cover;
          background-position: center 30%;
          background-repeat: no-repeat;
      }

      .features {
        text-align: left;        
        font-family: Arial, sans-serif;
        padding: 20px;
      }

      .features p {
        font-size: 1rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();

    setTitle("");
    trackPage();

    this.loadData();
    this.timer = setInterval(() => this.loadData(false), 10000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  /**
   * Animates the total miles count from the old value to the new value over a duration of 1 second.
   * @param {number} newTotal
   */
  animateTotalMiles(newTotal) {
    const oldTotal = this.totalMiles;
    const startTime = performance.now();
    const duration = 1000; // 1 second animation

    const animate = (/** @type {number} */ currentTime) => {
      const elapsed = currentTime - startTime;
      if (elapsed < duration) {
        this.totalMiles = Math.floor(
          oldTotal + (newTotal - oldTotal) * (elapsed / duration)
        );
        this.requestUpdate();
        requestAnimationFrame(animate);
      } else {
        this.totalMiles = newTotal;
        this.requestUpdate();
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Load the data.
   * @param {boolean} [firstLoad=true]
   */
  async loadData(firstLoad = true) {
    if (firstLoad) {
      this.landings = await fetchLatestLandings();
      this.latestFlights = await fetchLatestFlights();
    }

    const newTotal = await fetchTotalMiles();
    this.animateTotalMiles(newTotal);
  }

  download() {
    window.open(DOWNLOAD_URL, "_blank");
  }

  render() {
    return html`
      <navbar-component></navbar-component>
      <section class="hero">
        <div class="content">
          <h1 class="hero-title">Xplane Online Multiplayer</h1>
          <p class="hero-subtitle">
            <div class="bubble"><span style="color: yellow;">${this.totalMiles.toLocaleString()}</span> miles flown by our users and counting! ✈️</div>
          </p>
          <button-component @click=${this.download}
            >>> Download Now <<
          </button-component>
        </div>
      </section>
      <section>
        <div class="content">
          ${
            this.latestFlights.length > 0
              ? html`<h2>Active Flights</h2>
          ${this.latestFlights.map((flight) => {
            return html`<flight-component .flight=${flight}></flight-component>`;
          })}`
              : ""
          }
          ${
            this.landings.length > 0
              ? html`
            <h2>Latest Arrivals</h2>
          ${this.landings.map((landing) => {
            return html`<landing-component .landing=${landing}></landing-component>`;
          })}
            `
              : ""
          }
        </div>
      </section>
      <section>
        <div class="content features">
          <h2>What is Plane Pals?</h2>
          <p>Plane Pals is a multiplayer plugin that uses the <a href="https://github.com/TwinFan/XPMP2/blob/master/LICENSE">XPMP2 library</a> and adds a whole new dimension to X-Plane.</p>
          <h4>👋 Join Group Flights</h4>
          <p>Coordinate with friends or fellow simmers for shared flights, from short hops to long hauls.</p>
          <h4>👨‍✈️ Add Pals</h4>
          <p>Build your own network of virtual co-pilots by adding pals you meet during your flights.</p>
          <h4>🌏 Enjoy a Fuller Sky</h4>
          <p>See other pilots in real-time, making the skies feel more alive and immersive.</p>
          <h4>📍Points of Interest</h4>
          <p>Discover popular airports, landmarks, and scenic routes during your flights.</p>
          <h4>🛬 Landing Calculator</h4>
          <p>Get real-time feedback on your landing performance, including touchdown speed and flare timing.</p>
          <h4>🏆 XP System</h4>
          <p>Earn XP for flying and landing, and level up to unlock new roles in the Discord.</p>
        </div>
      </section>
    `;
  }
}

customElements.define("home-page", HomePage);
